import React from 'react';
import {
  Box,
  VStack,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Divider,
  List,
  ListItem,
} from '@chakra-ui/react';
import UserSettingsLayout from './UserSettingsLayout';


const FAQ_ITEMS = [
  {
    question: 'Is my data used to train AI models?',
    answer: 'Pabs uses OpenAI’s API to provide AI features. OpenAI does not use API data to train its models by default unless the account holder explicitly opts in. API inputs and outputs may be retained by OpenAI for a limited period for safety and abuse monitoring, subject to OpenAI’s current API data controls.',
  },
  {
    question: 'Who can see my plans and strategies?',
    answer: 'Only you can see your plans unless you explicitly share them. When you create a shareable link, only people with that specific link can view the shared content in read-only mode. You can revoke access at any time.',
  },
  {
    question: 'How long is my data stored?',
    answer: 'Plans and account data are stored while your account is active. Connector responses are used transiently; Pabs stores compact signals and provenance instead of full copies. Disconnecting a connector deletes its credentials, bindings, checks, signals, and call metadata from Pabs. Account deletion removes associated Pabs data, subject to any backups or records we must retain for security, billing, or legal reasons.',
  },
  {
    question: 'Is my data encrypted?',
    answer: 'Data is protected in transit using TLS. Passwords are hashed with bcrypt and are not stored in plain text. Connector access and refresh tokens are encrypted using AES-256-GCM before being stored.',
  },
  {
    question: 'Do you share my data with third parties?',
    answer: 'We do not sell your personal data. We use service providers needed to operate Pabs, including AI, hosting, database, email, billing, and any connector provider you choose to authorize. Connector data is sent only to the relevant provider and to the AI services required to answer your request or generate a Pulse or email.',
  },
  {
    question: 'Can I export my data?',
    answer: 'Yes. You can export your plans and strategies using the Ship feature, which generates documents in various formats. If you need a complete data export, please contact our support team.',
  },
  {
    question: 'What happens to shared links?',
    answer: 'Shared links provide read-only access to your plan. They do not expose your account information. You can deactivate any shared link at any time from the Share settings in your plan, and previously shared links will immediately stop working.',
  },
  {
    question: 'How do I delete my account and data?',
    answer: 'To delete your account and all associated data, please contact our support team. Upon account deletion, all your plans, strategies, and personal information will be permanently removed from our systems.',
  },
];

const DataPrivacy = () => {
  return (
    <UserSettingsLayout>
      <Box width="100%" bg="white" px={{ base: 5, md: 10 }} py={8}>
        <Box mb={8} maxW="720px">
          <Text fontSize="xl" fontWeight="700" color="brand.700">Data & Privacy</Text>
          <Text fontSize="sm" color="gray.900" mt={1}>
            Learn how we handle your data and protect your privacy.
          </Text>
        </Box>
        <VStack align="flex-start" width="100%" maxW="720px" spacing={6}>

          <Box width="100%">
            <Text fontSize="md" fontWeight="700" color="gray.800" mb={4}>How We Use Your Data</Text>
            <VStack align="flex-start" spacing={4} color="gray.700">
              <Box>
                <Text fontWeight="600" mb={1}>Your Plans & Content</Text>
                <Text fontSize="sm">
                  The plans, strategies, and content you create are stored securely in our database. 
                  This data is used solely to provide you with the PABS service and is never sold or 
                  shared with third parties.
                </Text>
              </Box>
              
              <Box>
                <Text fontWeight="600" mb={1}>AI Processing</Text>
                <Text fontSize="sm">
                  When you use AI features, relevant plan content and connector-derived
                  signals may be sent to OpenAI&apos;s API to generate a response, Pulse,
                  or email. OpenAI does not train on API data by default. OpenAI may retain
                  API inputs and outputs for a limited period under its current API data
                  controls unless a different retention arrangement applies.
                </Text>
              </Box>

              <Box>
                <Text fontWeight="600" mb={1}>Connected Knowledge Sources</Text>
                <Text fontSize="sm">
                  If you connect GitHub, Notion, Granola, or another knowledge source,
                  Pabs accesses it only with the permissions you authorize. Raw responses
                  are used at request time and are not retained as complete copies. Pabs
                  keeps limited provenance and normalized signals, such as that delivery
                  activity occurred, so Pulse and Engagement can explain what informed them.
                </Text>
              </Box>
              
              <Box>
                <Text fontWeight="600" mb={1}>Account Information</Text>
                <Text fontSize="sm">
                  We collect basic account information (email, name) to provide and improve our service. 
                  We do not share this information with third parties except as necessary to provide 
                  the service (e.g., email delivery).
                </Text>
              </Box>
            </VStack>
          </Box>

          <Divider />

          <Box width="100%">
            <Text fontSize="md" fontWeight="700" color="gray.800" mb={4}>Our Commitments</Text>
            <List spacing={3}>
              <ListItem display="flex" alignItems="flex-start">
                <Box as="span" mr={3} mt={1}>
                  <span className="material-icons-outlined" style={{ fontSize: '18px', color: '#38A169' }}>
                    check_circle
                  </span>
                </Box>
                <Text fontSize="sm">
                  <strong>No AI Training:</strong> Your data is never used to train AI models
                </Text>
              </ListItem>
              <ListItem display="flex" alignItems="flex-start">
                <Box as="span" mr={3} mt={1}>
                  <span className="material-icons-outlined" style={{ fontSize: '18px', color: '#38A169' }}>
                    check_circle
                  </span>
                </Box>
                <Text fontSize="sm">
                  <strong>No Data Selling:</strong> We never sell your personal information or content
                </Text>
              </ListItem>
              <ListItem display="flex" alignItems="flex-start">
                <Box as="span" mr={3} mt={1}>
                  <span className="material-icons-outlined" style={{ fontSize: '18px', color: '#38A169' }}>
                    check_circle
                  </span>
                </Box>
                <Text fontSize="sm">
                  <strong>Credential Protection:</strong> Connector tokens are encrypted before storage
                </Text>
              </ListItem>
              <ListItem display="flex" alignItems="flex-start">
                <Box as="span" mr={3} mt={1}>
                  <span className="material-icons-outlined" style={{ fontSize: '18px', color: '#38A169' }}>
                    check_circle
                  </span>
                </Box>
                <Text fontSize="sm">
                  <strong>Your Control:</strong> Disconnecting a source removes its credentials and connector-derived records
                </Text>
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box width="100%">
            <Text fontSize="md" fontWeight="700" color="gray.800" mb={4}>Frequently Asked Questions</Text>
            <Accordion allowMultiple>
              {FAQ_ITEMS.map((item, index) => (
                <AccordionItem key={index} border="none" mb={2}>
                  <AccordionButton 
                    bg="gray.50" 
                    borderRadius="md" 
                    _hover={{ bg: 'gray.100' }}
                    _expanded={{ bg: 'blue.50', borderBottomRadius: 0 }}
                  >
                    <Box flex="1" textAlign="left" fontWeight="500" fontSize="sm">
                      {item.question}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel 
                    pb={4} 
                    bg="gray.50" 
                    borderBottomRadius="md"
                    fontSize="sm"
                    color="gray.700"
                  >
                    {item.answer}
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </Box>

          <Box width="100%" pt={4}>
            <Text fontSize="sm" color="gray.500">
              Last updated: June 2026. If you have questions about our data practices,
              please contact our support team.
            </Text>
          </Box>
        </VStack>
      </Box>
    </UserSettingsLayout>
  );
};

export default DataPrivacy;
